import { useMemo, useState } from 'react';
import type { PlanFile, PlanStep, PlanStepType, ValidationIssue } from '@verifyloop/core';
import { PLAN_STEP_TYPE_OPTIONS, localPlanIssues } from '../lib/plan';
import { fieldIssues, indexIssues, IssueList, stepIssues } from './ValidationIssues';

/**
 * The plan editor.
 *
 * A Verifyloop plan is the durable artifact: a person or an agent writes it once,
 * in plain language, and every later run replays it. So the editor treats a step
 * as a sentence with a type and an optional target (not as code) and keeps the
 * JSON import/export honest, because the file has to survive a round trip
 * through `verifyloop test create --plan-from`.
 */
export function PlanEditor({
  plan,
  onChange,
  serverIssues = [],
  disabled = false,
}: {
  plan: PlanFile;
  onChange: (next: PlanFile) => void;
  /** Issues returned by the server on the last create/replace attempt. */
  serverIssues?: readonly ValidationIssue[];
  disabled?: boolean;
}): JSX.Element {
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteError, setPasteError] = useState<string | null>(null);

  /**
   * Two validators, one list. `validatePlan` ships in `@verifyloop/core`, so the
   * editor can flag a bad step as you type using exactly the rules the server
   * will apply; the server's own issues are merged in so a rejected save is
   * pinned to the same fields rather than dumped in a banner.
   */
  const allIssues = useMemo<ValidationIssue[]>(
    () => [...serverIssues, ...localPlanIssues(plan)],
    [serverIssues, plan],
  );
  const index = useMemo(() => indexIssues(allIssues), [allIssues]);

  const patch = (partial: Partial<PlanFile>): void => onChange({ ...plan, ...partial });

  const setSteps = (steps: PlanStep[]): void => patch({ planSteps: steps });

  const updateStep = (position: number, partial: Partial<PlanStep>): void => {
    setSteps(
      plan.planSteps.map((step, i) => (i === position ? { ...step, ...partial } : step)),
    );
  };

  const move = (position: number, delta: number): void => {
    const target = position + delta;
    if (target < 0 || target >= plan.planSteps.length) return;
    const steps = [...plan.planSteps];
    const a = steps[position];
    const b = steps[target];
    if (!a || !b) return;
    steps[position] = b;
    steps[target] = a;
    setSteps(steps);
  };

  const removeStep = (position: number): void => {
    setSteps(plan.planSteps.filter((_, i) => i !== position));
  };

  const addStep = (type: PlanStepType = 'action'): void => {
    setSteps([...plan.planSteps, { type, description: '' }]);
  };

  const applyPaste = (): void => {
    try {
      const parsed = JSON.parse(pasteText) as Partial<PlanFile>;
      const next = {
        ...plan,
        ...parsed,
        type: parsed.type === 'backend' ? 'backend' : plan.type,
        name: typeof parsed.name === 'string' ? parsed.name : plan.name,
        planSteps: Array.isArray(parsed.planSteps) ? parsed.planSteps : plan.planSteps,
      };
      onChange(next);
      setPasteOpen(false);
      setPasteError(null);
    } catch (error) {
      setPasteError(error instanceof Error ? error.message : 'that is not valid JSON');
    }
  };

  return (
    <div className="plan-editor">
      <div className="plan-editor__toolbar">
        <div className="plan-editor__counts">
          <span>
            <strong>{plan.planSteps.length}</strong> step{plan.planSteps.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="plan-editor__actions">
          <button
            type="button"
            className="btn btn--ghost btn--small"
            disabled={disabled}
            onClick={() => {
              setPasteText(JSON.stringify({ ...plan, planSteps: plan.planSteps }, null, 2));
              setPasteError(null);
              setPasteOpen(true);
            }}
          >
            Paste a plan
          </button>
        </div>
      </div>

      <IssueList issues={allIssues} />

      <ol className="plan-steps" start={1}>
        {plan.planSteps.map((step, position) => (
          <PlanStepRow
            key={position}
            step={step}
            position={position}
            total={plan.planSteps.length}
            issues={stepIssues(index, position)}
            descriptionIssues={fieldIssues(index, `$.planSteps[${position}].description`)}
            targetIssues={fieldIssues(index, `$.planSteps[${position}].target`)}
            disabled={disabled}
            onChange={(partial) => updateStep(position, partial)}
            onMove={(delta) => move(position, delta)}
            onRemove={() => removeStep(position)}
          />
        ))}
      </ol>

      <div className="plan-editor__footer">
        <button
          type="button"
          className="btn btn--small"
          disabled={disabled}
          onClick={() => addStep('action')}
        >
          + Add step
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--small"
          disabled={disabled}
          onClick={() => addStep('assertion')}
        >
          + Add assertion
        </button>
      </div>

      {pasteOpen ? (
        <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && setPasteOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="paste-plan-title">
            <header className="modal__header">
              <h2 id="paste-plan-title">Paste a plan</h2>
              <button
                type="button"
                className="btn btn--ghost btn--icon"
                onClick={() => setPasteOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </header>
            <div className="modal__body">
              <p className="muted">
                A plan file is <code>type</code>, <code>name</code> and <code>planSteps</code>. Paste the
                whole object or just the steps array.
              </p>
              <textarea
                className="input input--area input--code"
                rows={14}
                value={pasteText}
                spellCheck={false}
                aria-label="Plan JSON"
                onChange={(event) => setPasteText(event.target.value)}
              />
              {pasteError ? (
                <p className="field__error" role="alert">
                  {pasteError}
                </p>
              ) : null}
            </div>
            <footer className="modal__footer">
              <button type="button" className="btn btn--ghost" onClick={() => setPasteOpen(false)}>
                Cancel
              </button>
              <button type="button" className="btn" onClick={applyPaste}>
                Apply plan
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PlanStepRow({
  step,
  position,
  total,
  issues,
  descriptionIssues,
  targetIssues,
  disabled,
  onChange,
  onMove,
  onRemove,
}: {
  step: PlanStep;
  position: number;
  total: number;
  issues: readonly ValidationIssue[];
  descriptionIssues: readonly ValidationIssue[];
  targetIssues: readonly ValidationIssue[];
  disabled: boolean;
  onChange: (partial: Partial<PlanStep>) => void;
  onMove: (delta: number) => void;
  onRemove: () => void;
}): JSX.Element {
  const descriptionId = `plan-step-${position}-description`;
  const targetId = `plan-step-${position}-target`;
  const hasError = issues.some((issue) => issue.severity === 'error');

  return (
    <li className={`plan-step${hasError ? ' plan-step--invalid' : ''}`}>
      <div className="plan-step__rail">
        <span className="plan-step__index" aria-hidden="true">
          {position + 1}
        </span>
        <div className="plan-step__order">
          <button
            type="button"
            className="btn btn--icon btn--ghost"
            disabled={disabled || position === 0}
            onClick={() => onMove(-1)}
            aria-label={`Move step ${position + 1} earlier`}
          >
            ↑
          </button>
          <button
            type="button"
            className="btn btn--icon btn--ghost"
            disabled={disabled || position === total - 1}
            onClick={() => onMove(1)}
            aria-label={`Move step ${position + 1} later`}
          >
            ↓
          </button>
        </div>
      </div>

      <div className="plan-step__body">
        <div className="plan-step__head">
          <label className="visually-hidden" htmlFor={`plan-step-${position}-type`}>
            Step {position + 1} type
          </label>
          <select
            id={`plan-step-${position}-type`}
            className="input input--select input--compact"
            value={step.type}
            disabled={disabled}
            onChange={(event) => onChange({ type: event.target.value as PlanStepType })}
          >
            {PLAN_STEP_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {step.optional ? <span className="pill pill--neutral pill--subtle">optional</span> : null}
        </div>

        <label className="visually-hidden" htmlFor={descriptionId}>
          Step {position + 1} description
        </label>
        <input
          id={descriptionId}
          className="input"
          type="text"
          value={step.description}
          disabled={disabled}
          placeholder="In plain language: what should happen here?"
          aria-invalid={descriptionIssues.some((issue) => issue.severity === 'error') || undefined}
          aria-describedby={descriptionIssues.length > 0 ? `${descriptionId}-error` : undefined}
          onChange={(event) => onChange({ description: event.target.value })}
        />
        {descriptionIssues.length > 0 ? (
          <p className="field__error" id={`${descriptionId}-error`}>
            {descriptionIssues.map((issue) => issue.message).join(' ')}
          </p>
        ) : null}

        <div className="plan-step__target">
          <label className="visually-hidden" htmlFor={targetId}>
            Step {position + 1} target
          </label>
          <input
            id={targetId}
            className="input input--compact"
            type="text"
            value={step.target ?? ''}
            disabled={disabled}
            placeholder="target: URL, selector, or {{placeholder}} (optional)"
            aria-invalid={targetIssues.some((issue) => issue.severity === 'error') || undefined}
            onChange={(event) => {
              const value = event.target.value;
              const partial: Partial<PlanStep> = {};
              if (value === '') delete partial.target;
              else partial.target = value;
              onChange(partial);
            }}
          />

          {step.type === 'wait' ? (
            <label className="plan-step__duration">
              <span className="visually-hidden">Duration in milliseconds</span>
              <input
                className="input input--compact"
                type="number"
                min={0}
                max={120000}
                step={100}
                disabled={disabled}
                value={step.durationMs ?? 1000}
                onChange={(event) => {
                  const parsed = Number(event.target.value);
                  onChange({ durationMs: Number.isFinite(parsed) ? parsed : undefined });
                }}
              />
              <span className="plan-step__duration-unit">ms</span>
            </label>
          ) : null}

          <label className="checkbox plan-step__optional">
            <input
              type="checkbox"
              checked={Boolean(step.optional)}
              disabled={disabled}
              onChange={(event) => onChange({ optional: event.target.checked || undefined })}
            />
            <span className="visually-hidden">Step {position + 1} is optional</span>
            <span aria-hidden="true">optional</span>
          </label>

          <button
            type="button"
            className="btn btn--ghost btn--small plan-step__remove"
            disabled={disabled}
            onClick={onRemove}
            aria-label={`Delete step ${position + 1}`}
          >
            Delete
          </button>
        </div>

        {issues.length > 0 ? <IssueList issues={issues} dense /> : null}
      </div>
    </li>
  );
}